import * as React from 'react'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import type { LabelDocument } from '@/lib/db/rxdb/schemas/label'
import type { LabelGroupDocument } from '@/lib/db/rxdb/schemas/labelGroup'
import type { ProjectDocument } from '@/lib/db/rxdb/schemas/project'
import { BaseFormDialog } from '@/components/common/BaseFormDialog'
import { ColorPicker, PRESET_COLORS } from '@/components/app/pickers/color-picker'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import useLoginStore from '@/store/LoginStore'
import { useLabelMutations } from '@/lib/db/rxdb/hooks'
import { WORKSPACE_TYPE_VALUES } from '@/lib/types/subscription'
import { groupProjectsBySpace } from '@/lib/utils/groupProjectsBySpace'

// Re-export for any external consumers
export { PRESET_COLORS }

interface CreateLabelDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  projectId?: string | null
  projects?: Array<ProjectDocument>
  labelGroups?: Array<LabelGroupDocument>
  existingLabels?: Array<{ id: string; name: string }>
  mode?: 'create' | 'edit'
  initialLabel?: LabelDocument | null
  /** Seeds the name in create mode, e.g. from a picker's `Create "…"` row. */
  initialName?: string
  onSuccess?: (label: LabelDocument) => void
}

// Validation schema for creating/editing labels
type TFunc = (key: string) => string

const buildLabelFormSchema = (t: TFunc) =>
  z.object({
    name: z
      .string()
      .min(3, t('labels.formErrors.nameMin'))
      .max(50, t('labels.formErrors.nameMax')),
    description: z
      .string()
      .max(200, t('labels.formErrors.descriptionMax'))
      .optional(),
    color: z.string(),
    projectId: z.string().nullable(),
    labelGroupId: z.string().nullable(),
  })

type LabelFormData = z.infer<ReturnType<typeof buildLabelFormSchema>>

/**
 * CreateLabelDialog - Dialog for creating new labels
 *
 * @example
 * <CreateLabelDialog
 *   open={isOpen}
 *   onOpenChange={setIsOpen}
 *   projectId={currentProjectId}
 * />
 */
export function CreateLabelDialog({
  open,
  onOpenChange,
  projectId,
  projects = [],
  labelGroups = [],
  existingLabels = [],
  mode = 'create',
  initialLabel = null,
  initialName = '',
  onSuccess,
}: CreateLabelDialogProps) {
  const { t } = useTranslation('settings')
  const { workspaceId, workspaceType } = useLoginStore()
  const { createLabel, updateLabel } = useLabelMutations()
  const [duplicateError, setDuplicateError] = React.useState<string | null>(null)

  const isEditMode = mode === 'edit' && !!initialLabel
  const workspaceOptionValue = '__workspace__'

  const labelFormSchema = React.useMemo(() => buildLabelFormSchema(t), [t])
  const form = useForm<LabelFormData>({
    resolver: zodResolver(labelFormSchema),
    defaultValues: {
      name: '',
      description: '',
      color: PRESET_COLORS[0],
      projectId: null,
      labelGroupId: null,
    },
  })

  const selectedProjectId = form.watch('projectId')

  const availableGroups = React.useMemo(() => {
    return labelGroups.filter((group) => {
      if (selectedProjectId) {
        return group.projectId === selectedProjectId
      }

      return !group.projectId
    })
  }, [labelGroups, selectedProjectId])

  const projectScopeGroups = React.useMemo(
    () =>
      groupProjectsBySpace(projects).map((g) => ({
        spaceLabel: g.space?.name || (g.space ? t('labels.dialog.untitledSpace') : null),
        options: g.children.map((p) => ({
          value: p.id,
          label: p.name || t('labels.dialog.untitledProject'),
        })),
      })),
    [projects, t],
  )

  const isSelectableProjectId = React.useCallback(
    (id: string | null | undefined) =>
      id != null &&
      projectScopeGroups.some((g) => g.options.some((o) => o.value === id)),
    [projectScopeGroups],
  )

  const hasSelectableProject = projectScopeGroups.some((g) => g.options.length > 0)
  const hasLegacyValue =
    selectedProjectId != null && !isSelectableProjectId(selectedProjectId)
  const showScopeUI = hasSelectableProject || hasLegacyValue

  React.useEffect(() => {
    if (open && isEditMode && initialLabel) {
      setDuplicateError(null)
      form.reset({
        name: initialLabel.name,
        description: initialLabel.description ?? '',
        color: initialLabel.color,
        projectId: initialLabel.projectId ?? null,
        labelGroupId: initialLabel.labelGroupId ?? null,
      })
      return
    }

    if (open && !isEditMode) {
      setDuplicateError(null)
      form.reset({
        name: initialName,
        description: '',
        color: PRESET_COLORS[0],
        // Always seed projectId to null on full reset; the narrow effect below
        // promotes the prop to the field only if it's a real (selectable) project.
        projectId: null,
        labelGroupId: null,
      })
    }

    if (!open) {
      setDuplicateError(null)
      form.reset()
    }
  }, [open, isEditMode, initialLabel, initialName, projectId, form])

  // Narrow seed effect: in create mode, if the dialog received a `projectId`
  // prop and that ID is a real selectable project (not a space, not deleted),
  // populate the form field. Runs again when groups arrive after open. Never
  // overwrites a user's manual choice.
  React.useEffect(() => {
    if (!open || isEditMode) return
    if (form.getFieldState('projectId').isDirty) return
    if (form.getValues('projectId') != null) return
    if (!isSelectableProjectId(projectId)) return
    form.setValue('projectId', projectId ?? null, { shouldDirty: false })
  }, [open, isEditMode, projectId, isSelectableProjectId, form])

  const handleSubmit = async (data: LabelFormData) => {
    if (!workspaceId) {
      return
    }

    const trimmedName = data.name.trim()

    // Check for duplicate label name (case-insensitive)
    const isDuplicate = existingLabels.some(
      (l) =>
        l.name.toLowerCase() === trimmedName.toLowerCase() &&
        l.id !== initialLabel?.id,
    )
    if (isDuplicate) {
      setDuplicateError(t('labels.dialog.duplicateError'))
      return
    }
    setDuplicateError(null)

    try {
      const payload = {
        name: trimmedName,
        color: data.color,
        description: data.description?.trim() || null,
        projectId: data.projectId || null,
        labelGroupId: data.labelGroupId || null,
        sortOrder: null,
      }

      if (isEditMode && initialLabel) {
        const updatedLabel = await updateLabel(initialLabel.id, payload)
        if (onSuccess && updatedLabel) {
          onSuccess(updatedLabel as LabelDocument)
        }
      } else {
        const newLabel = await createLabel(payload)

        // Wait a moment for RxDB to sync to backend
        // This ensures the label exists in the backend before we try to assign it
        await new Promise((resolve) => setTimeout(resolve, 500))

        if (onSuccess) {
          onSuccess(newLabel)
        }
      }

      form.reset()
      onOpenChange(false)
    } catch (error) {
      // Error handling
      // TODO: Show error toast
    }
  }

  const dialogTitle = isEditMode ? t('labels.dialog.editTitle') : t('labels.dialog.createTitle')
  const dialogDescription = isEditMode
    ? t('labels.dialog.editDescription')
    : t('labels.dialog.createDescription')
  const submitLabel = isEditMode ? t('labels.dialog.editSubmit') : t('labels.dialog.createSubmit')

  return (
    <BaseFormDialog
      open={open}
      onOpenChange={onOpenChange}
      title={dialogTitle}
      description={dialogDescription}
      submitLabel={submitLabel}
      isSubmitting={form.formState.isSubmitting}
      submitDisabled={!form.formState.isValid || form.formState.isSubmitting}
      maxWidth="md"
    >
      <form id="dialog-form" onSubmit={form.handleSubmit(handleSubmit)}>
        <div className="space-y-6 py-4">
          <div className="space-y-2">
            <Label htmlFor="label-name">
              {t('labels.dialog.nameLabel')} <span className="text-destructive">*</span>
            </Label>
            <Input
              id="label-name"
              {...form.register('name')}
              placeholder={t('labels.dialog.namePlaceholder')}
              maxLength={50}
              autoFocus
            />
            {(form.formState.errors.name || duplicateError) && (
              <p className="text-sm text-destructive">
                {form.formState.errors.name?.message ?? duplicateError}
              </p>
            )}
          </div>

          {showScopeUI && (
            <div className="space-y-3">
              <div className="flex items-center justify-between text-sm font-medium text-foreground">
                <span>{t('labels.dialog.scopeAndGroup')}</span>
              </div>
              <p className="text-xs text-muted-foreground">
                {workspaceType === WORKSPACE_TYPE_VALUES.TEAM
                  ? t('labels.dialog.scopeHelpTeam')
                  : t('labels.dialog.scopeHelpPersonal')}
              </p>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="min-w-0 space-y-2">
                  <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                    {t('labels.dialog.scopeLabel')}
                  </Label>
                  <Select
                    value={selectedProjectId ?? workspaceOptionValue}
                    onValueChange={(value) => {
                      form.setValue(
                        'projectId',
                        value === workspaceOptionValue ? null : value,
                        { shouldDirty: true },
                      )
                      form.setValue('labelGroupId', null)
                    }}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder={t('labels.dialog.selectScope')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={workspaceOptionValue}>{t('labels.dialog.workspace')}</SelectItem>
                      {projectScopeGroups.map((group, idx) => (
                        <SelectGroup key={group.spaceLabel ?? `__orphan_${idx}__`}>
                          <SelectLabel>{group.spaceLabel ?? t('labels.dialog.otherSpace')}</SelectLabel>
                          {group.options.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectGroup>
                      ))}
                    </SelectContent>
                  </Select>
                  {hasLegacyValue && (
                    <p className="text-xs text-muted-foreground">
                      {t('labels.dialog.legacyHint')}
                    </p>
                  )}
                </div>
                <div className="min-w-0 space-y-2">
                  <Label className="text-xs uppercase tracking-wide text-muted-foreground">
                    {t('labels.dialog.groupLabel')}
                  </Label>
                  <Select
                    value={form.watch('labelGroupId') ?? 'none'}
                    onValueChange={(value) =>
                      form.setValue('labelGroupId', value === 'none' ? null : value)
                    }
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder={t('labels.dialog.selectGroup')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">{t('labels.dialog.groupNone')}</SelectItem>
                      {availableGroups.map((group) => (
                        <SelectItem key={group.id} value={group.id}>
                          {group.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          )}

          <div className="space-y-3">
            <Label className="text-sm font-medium text-foreground">
              {t('labels.dialog.colorPreviewLabel')}
            </Label>
            <ColorPicker
              value={form.watch('color')}
              onChange={(color) => form.setValue('color', color ?? PRESET_COLORS[0])}
            />
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center gap-1.5 rounded-full border border-input bg-card px-2.5 py-1 text-sm font-medium text-foreground">
                <span
                  className="size-2 rounded-full"
                  style={{ backgroundColor: form.watch('color') }}
                />
                {form.watch('name') || t('labels.dialog.previewName')}
              </span>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="label-description">{t('labels.dialog.descriptionLabel')}</Label>
            <textarea
              id="label-description"
              {...form.register('description')}
              placeholder={t('labels.dialog.descriptionPlaceholder')}
              maxLength={200}
              rows={3}
              className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"
            />
            {form.formState.errors.description && (
              <p className="text-sm text-destructive">
                {form.formState.errors.description.message}
              </p>
            )}
          </div>
        </div>
      </form>
    </BaseFormDialog>
  )
}
